/**
 * Serviço de WebGL Fingerprinting
 * Responsável por gerar fingerprints únicos usando WebGL
 */

import { CanvasFactory } from '../utils/CanvasFactory.js';

export class WebGLService {
    constructor(config, logger) {
        this.config = config;
        this.logger = logger.child('WebGLService');
        this.cache = new Map();
        this.stats = {
            calls: 0,
            errors: 0,
            cacheHits: 0,
            avgExecutionTime: 0
        };
        this.glContext = null;
        this.canvas = null;
    }

    /**
     * Obtém fingerprint do WebGL
     */
    async getFingerprint() {
        if (!this.config.webglEnabled) {
            this.logger.debug('WebGL fingerprinting desabilitado');
            return { enabled: false };
        }

        const startTime = performance.now();
        this.stats.calls++;

        try {
            // Verifica cache primeiro
            const cacheKey = 'webgl_fingerprint';
            if (this.cache.has(cacheKey)) {
                this.stats.cacheHits++;
                this.logger.debug('WebGL fingerprint obtido do cache');
                return this.cache.get(cacheKey);
            }

            const fingerprint = await this.generateWebGLFingerprint();

            // Armazena no cache
            this.cache.set(cacheKey, fingerprint);

            const executionTime = performance.now() - startTime;
            this.updateStats(executionTime);

            this.logger.debug('WebGL fingerprint gerado', {
                executionTime: `${executionTime.toFixed(2)}ms`,
                renderer: fingerprint.renderer
            });

            return fingerprint;

        } catch (error) {
            this.stats.errors++;
            this.logger.error('Erro ao gerar WebGL fingerprint', { error: error.message });
            throw error;
        }
    }

    /**
     * Gera fingerprint completo do WebGL
     */
    async generateWebGLFingerprint() {
        const gl = this.getWebGLContext();

        if (!gl) {
            throw new Error('WebGL não disponível');
        }

        const fingerprint = {
            // Informações básicas
            version: this.getWebGLVersion(gl),
            vendor: this.getParameter(gl, gl.VENDOR),
            renderer: this.getParameter(gl, gl.RENDERER),

            // Versões e extensões
            glsl_version: this.getParameter(gl, gl.SHADING_LANGUAGE_VERSION),
            extensions: this.getExtensions(gl),

            // Parâmetros do contexto
            parameters: this.getContextParameters(gl),

            // Capacidades e limites
            capabilities: this.getCapabilities(gl),

            // Fingerprint visual
            visual_fingerprint: await this.generateVisualFingerprint(gl),

            // Informações de precisão
            precision: this.getPrecisionInfo(gl),

            // Detecção de virtualização
            virtualization_hints: this.detectVirtualization(gl),

            // Hash único
            hash: null // Será calculado no final
        };

        // Calcula hash do fingerprint completo
        fingerprint.hash = await this.hashFingerprint(fingerprint);

        return fingerprint;
    }

    /**
     * Obtém contexto WebGL
     */
    getWebGLContext() {
        if (this.glContext) {
            return this.glContext;
        }

        if (!this.canvas) {
            this.canvas = CanvasFactory.createCanvas(256, 128);
            this.canvas.width = 256;
            this.canvas.height = 128;
        }

        const contextOptions = {
            antialias: false,
            depth: true,
            stencil: true,
            preserveDrawingBuffer: true,
            failIfMajorPerformanceCaveat: false
        };

        // Tenta WebGL2 primeiro, depois WebGL1
        this.glContext = this.canvas.getContext('webgl2', contextOptions) ||
            this.canvas.getContext('webgl', contextOptions) ||
            this.canvas.getContext('experimental-webgl', contextOptions);

        return this.glContext;
    }

    /**
     * Obtém versão do WebGL
     */
    getWebGLVersion(gl) {
        if (gl instanceof WebGL2RenderingContext) {
            return 'WebGL 2.0';
        } else if (gl instanceof WebGLRenderingContext) {
            return 'WebGL 1.0';
        }
        return 'Unknown';
    }

    /**
     * Obtém parâmetro do WebGL com tratamento de erro
     */
    getParameter(gl, parameter) {
        try {
            return gl.getParameter(parameter);
        } catch (error) {
            this.logger.debug(`Erro ao obter parâmetro ${parameter}`, { error: error.message });
            return null;
        }
    }

    /**
     * Obtém todas as extensões disponíveis
     */
    getExtensions(gl) {
        const extensions = gl.getSupportedExtensions() || [];
        const extensionData = {};

        extensions.forEach(name => {
            try {
                const ext = gl.getExtension(name);
                extensionData[name] = {
                    available: !!ext,
                    constants: this.getExtensionConstants(gl, ext, name)
                };
            } catch (error) {
                extensionData[name] = {
                    available: false,
                    error: error.message
                };
            }
        });

        return {
            supported: extensions,
            count: extensions.length,
            details: extensionData
        };
    }

    /**
     * Obtém constantes específicas de extensões
     */
    getExtensionConstants(gl, ext, name) {
        const constants = {};

        try {
            switch (name) {
                case 'WEBGL_debug_renderer_info':
                    constants.UNMASKED_VENDOR = this.getParameter(gl, ext.UNMASKED_VENDOR_WEBGL);
                    constants.UNMASKED_RENDERER = this.getParameter(gl, ext.UNMASKED_RENDERER_WEBGL);
                    break;

                case 'EXT_texture_filter_anisotropic':
                case 'WEBKIT_EXT_texture_filter_anisotropic':
                case 'MOZ_EXT_texture_filter_anisotropic':
                    constants.MAX_TEXTURE_MAX_ANISOTROPY = this.getParameter(gl, ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
                    break;

                case 'WEBGL_draw_buffers':
                    constants.MAX_DRAW_BUFFERS = this.getParameter(gl, ext.MAX_DRAW_BUFFERS_WEBGL);
                    constants.MAX_COLOR_ATTACHMENTS = this.getParameter(gl, ext.MAX_COLOR_ATTACHMENTS_WEBGL);
                    break;
            }
        } catch (error) {
            this.logger.debug(`Erro ao obter constantes da extensão ${name}`, { error: error.message });
        }

        return constants;
    }

    /**
     * Obtém parâmetros importantes do contexto
     */
    getContextParameters(gl) {
        const parameters = {};

        const parameterList = [
            'MAX_TEXTURE_SIZE', 'MAX_CUBE_MAP_TEXTURE_SIZE', 'MAX_RENDERBUFFER_SIZE',
            'MAX_VIEWPORT_DIMS', 'MAX_VERTEX_ATTRIBS', 'MAX_VERTEX_UNIFORM_VECTORS',
            'MAX_FRAGMENT_UNIFORM_VECTORS', 'MAX_VARYING_VECTORS', 'MAX_TEXTURE_IMAGE_UNITS',
            'MAX_VERTEX_TEXTURE_IMAGE_UNITS', 'MAX_COMBINED_TEXTURE_IMAGE_UNITS',
            'ALIASED_LINE_WIDTH_RANGE', 'ALIASED_POINT_SIZE_RANGE',
            'RED_BITS', 'GREEN_BITS', 'BLUE_BITS', 'ALPHA_BITS',
            'DEPTH_BITS', 'STENCIL_BITS', 'SUBPIXEL_BITS'
        ];

        parameterList.forEach(param => {
            try {
                parameters[param] = this.getParameter(gl, gl[param]);
            } catch (error) {
                parameters[param] = null;
            }
        });

        return parameters;
    }

    /**
     * Obtém capacidades avançadas
     */
    getCapabilities(gl) {
        return {
            // Formatos de textura suportados
            texture_formats: this.getSupportedTextureFormats(gl),

            // Formatos de render buffer
            renderbuffer_formats: this.getSupportedRenderbufferFormats(gl),

            // Precisão de shaders
            shader_precision: this.getShaderPrecision(gl),

            // Informações de performance
            performance_info: this.getPerformanceInfo(gl)
        };
    }

    /**
     * Obtém formatos de textura suportados (otimizado para reduzir warnings)
     */
    getSupportedTextureFormats(gl) {
        const formats = [];
        const safeFormats = [
            'RGB', 'RGBA', 'LUMINANCE', 'LUMINANCE_ALPHA', 'ALPHA'
        ];

        // Clear any existing errors first
        gl.getError();

        safeFormats.forEach(format => {
            try {
                if (gl[format] !== undefined) {
                    // Only test basic internal formats that are widely supported
                    const internalFormat = gl[format];
                    const pixelFormat = gl[format];

                    // Check if format combination is valid before testing
                    if (this.isValidFormatCombination(internalFormat, pixelFormat)) {
                        const texture = gl.createTexture();
                        if (texture) {
                            gl.bindTexture(gl.TEXTURE_2D, texture);

                            // Use a small 1x1 pixel array instead of null for better compatibility
                            const pixel = new Uint8Array(4); // RGBA

                            try {
                                gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 1, 1, 0, pixelFormat, gl.UNSIGNED_BYTE, pixel);

                                const error = gl.getError();
                                if (error === gl.NO_ERROR) {
                                    formats.push(format);
                                }
                            } catch (textureError) {
                                // Safely ignore texture creation errors
                            }

                            gl.deleteTexture(texture);
                        }
                    }
                }
            } catch (error) {
                // Formato não suportado - silently continue
            }
        });

        return formats;
    }

    /**
     * Check if internal format and pixel format combination is valid
     */
    isValidFormatCombination(internalFormat, pixelFormat) {
        // Basic validation for common format combinations
        const validCombinations = new Map([
            [WebGLRenderingContext.RGB, WebGLRenderingContext.RGB],
            [WebGLRenderingContext.RGBA, WebGLRenderingContext.RGBA],
            [WebGLRenderingContext.LUMINANCE, WebGLRenderingContext.LUMINANCE],
            [WebGLRenderingContext.LUMINANCE_ALPHA, WebGLRenderingContext.LUMINANCE_ALPHA],
            [WebGLRenderingContext.ALPHA, WebGLRenderingContext.ALPHA]
        ]);

        return validCombinations.has(internalFormat) &&
            validCombinations.get(internalFormat) === pixelFormat;
    }

    /**
     * Obtém formatos de renderbuffer suportados
     */
    getSupportedRenderbufferFormats(gl) {
        const formats = [];
        const testFormats = [
            'RGBA4', 'RGB565', 'RGB5_A1', 'DEPTH_COMPONENT16',
            'STENCIL_INDEX8', 'DEPTH_STENCIL'
        ];

        testFormats.forEach(format => {
            try {
                if (gl[format] !== undefined) {
                    const rb = gl.createRenderbuffer();
                    gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
                    gl.renderbufferStorage(gl.RENDERBUFFER, gl[format], 1, 1);

                    if (gl.getError() === gl.NO_ERROR) {
                        formats.push(format);
                    }

                    gl.deleteRenderbuffer(rb);
                }
            } catch (error) {
                // Formato não suportado
            }
        });

        return formats;
    }

    /**
     * Obtém informações de precisão de shaders
     */
    getShaderPrecision(gl) {
        const precision = {};
        const shaderTypes = ['VERTEX_SHADER', 'FRAGMENT_SHADER'];
        const precisionTypes = ['LOW_FLOAT', 'MEDIUM_FLOAT', 'HIGH_FLOAT', 'LOW_INT', 'MEDIUM_INT', 'HIGH_INT'];

        shaderTypes.forEach(shaderType => {
            precision[shaderType] = {};

            precisionTypes.forEach(precisionType => {
                try {
                    const format = gl.getShaderPrecisionFormat(gl[shaderType], gl[precisionType]);
                    precision[shaderType][precisionType] = {
                        rangeMin: format.rangeMin,
                        rangeMax: format.rangeMax,
                        precision: format.precision
                    };
                } catch (error) {
                    precision[shaderType][precisionType] = null;
                }
            });
        });

        return precision;
    }

    /**
     * Obtém informações de performance
     */
    getPerformanceInfo(gl) {
        const info = {};

        try {
            // Testa criação de objetos
            const startTime = performance.now();

            const texture = gl.createTexture();
            const buffer = gl.createBuffer();
            const shader = gl.createShader(gl.VERTEX_SHADER);
            const program = gl.createProgram();

            info.object_creation_time = performance.now() - startTime;

            // Limpa objetos
            gl.deleteTexture(texture);
            gl.deleteBuffer(buffer);
            gl.deleteShader(shader);
            gl.deleteProgram(program);

            // Testa operações de desenho
            const drawStartTime = performance.now();
            gl.clear(gl.COLOR_BUFFER_BIT);
            info.clear_time = performance.now() - drawStartTime;

        } catch (error) {
            info.error = error.message;
        }

        return info;
    }

    /**
     * Gera fingerprint visual usando shaders
     */
    async generateVisualFingerprint(gl) {
        try {
            // Shader vertex simples
            const vertexShaderSource = `
                attribute vec2 a_position;
                varying vec2 v_texCoord;
                void main() {
                    gl_Position = vec4(a_position, 0.0, 1.0);
                    v_texCoord = a_position * 0.5 + 0.5;
                }
            `;

            // Shader fragment com padrão complexo
            const fragmentShaderSource = `
                precision mediump float;
                varying vec2 v_texCoord;
                void main() {
                    vec2 uv = v_texCoord;
                    float r = sin(uv.x * 10.0) * cos(uv.y * 10.0);
                    float g = sin(uv.x * 15.0 + 1.0) * cos(uv.y * 15.0 + 1.0);
                    float b = sin(uv.x * 20.0 + 2.0) * cos(uv.y * 20.0 + 2.0);
                    gl_FragColor = vec4(r * 0.5 + 0.5, g * 0.5 + 0.5, b * 0.5 + 0.5, 1.0);
                }
            `;

            const program = this.createShaderProgram(gl, vertexShaderSource, fragmentShaderSource);

            if (!program) {
                throw new Error('Falha ao criar programa de shader');
            }

            // Configura geometria
            const vertices = new Float32Array([
                -1, -1,
                1, -1,
                -1, 1,
                1, 1
            ]);

            const buffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
            gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

            // Configura programa
            gl.useProgram(program);
            const positionLocation = gl.getAttribLocation(program, 'a_position');
            gl.enableVertexAttribArray(positionLocation);
            gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

            // Renderiza
            gl.viewport(0, 0, this.canvas.width, this.canvas.height);
            gl.clearColor(0, 0, 0, 1);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

            // Obtém dados da imagem
            const pixels = new Uint8Array(this.canvas.width * this.canvas.height * 4);
            gl.readPixels(0, 0, this.canvas.width, this.canvas.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

            // Limpa recursos
            gl.deleteBuffer(buffer);
            gl.deleteProgram(program);

            // Calcula hash dos pixels
            const pixelHash = await this.hashPixels(pixels);

            return {
                canvas_data: this.canvas.toDataURL(),
                pixel_hash: pixelHash,
                pixel_checksum: this.calculatePixelChecksum(pixels),
                dimensions: {
                    width: this.canvas.width,
                    height: this.canvas.height
                }
            };

        } catch (error) {
            this.logger.error('Erro ao gerar fingerprint visual', { error: error.message });
            return { error: error.message };
        }
    }

    /**
     * Cria programa de shader
     */
    createShaderProgram(gl, vertexSource, fragmentSource) {
        const vertexShader = this.createShader(gl, gl.VERTEX_SHADER, vertexSource);
        const fragmentShader = this.createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);

        if (!vertexShader || !fragmentShader) {
            return null;
        }

        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            this.logger.error('Erro ao linkar programa', {
                error: gl.getProgramInfoLog(program)
            });
            gl.deleteProgram(program);
            return null;
        }

        gl.deleteShader(vertexShader);
        gl.deleteShader(fragmentShader);

        return program;
    }

    /**
     * Cria shader individual
     */
    createShader(gl, type, source) {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);

        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            this.logger.error('Erro ao compilar shader', {
                error: gl.getShaderInfoLog(shader)
            });
            gl.deleteShader(shader);
            return null;
        }

        return shader;
    }

    /**
     * Obtém informações de precisão
     */
    getPrecisionInfo(gl) {
        const info = {};

        try {
            // Testa diferentes tipos de precisão
            const precisionTests = [
                { shader: gl.VERTEX_SHADER, type: gl.HIGH_FLOAT, name: 'vertex_high_float' },
                { shader: gl.FRAGMENT_SHADER, type: gl.HIGH_FLOAT, name: 'fragment_high_float' },
                { shader: gl.VERTEX_SHADER, type: gl.HIGH_INT, name: 'vertex_high_int' },
                { shader: gl.FRAGMENT_SHADER, type: gl.HIGH_INT, name: 'fragment_high_int' }
            ];

            precisionTests.forEach(test => {
                const format = gl.getShaderPrecisionFormat(test.shader, test.type);
                info[test.name] = {
                    rangeMin: format.rangeMin,
                    rangeMax: format.rangeMax,
                    precision: format.precision
                };
            });

        } catch (error) {
            info.error = error.message;
        }

        return info;
    }

    /**
     * Detecta sinais de virtualização
     */
    detectVirtualization(gl) {
        const hints = [];

        try {
            const renderer = this.getParameter(gl, gl.RENDERER) || '';
            const vendor = this.getParameter(gl, gl.VENDOR) || '';

            // Verifica indicadores comuns de virtualização
            const virtualizationIndicators = [
                'VMware', 'VirtualBox', 'QEMU', 'Parallels',
                'Microsoft Basic Render', 'llvmpipe',
                'Software Rasterizer', 'Mesa'
            ];

            virtualizationIndicators.forEach(indicator => {
                if (renderer.includes(indicator) || vendor.includes(indicator)) {
                    hints.push(`Detected ${indicator} in ${renderer.includes(indicator) ? 'renderer' : 'vendor'}`);
                }
            });

            // Verifica limitações suspeitas
            const maxTextureSize = this.getParameter(gl, gl.MAX_TEXTURE_SIZE);
            if (maxTextureSize && maxTextureSize < 2048) {
                hints.push('Suspiciously low MAX_TEXTURE_SIZE');
            }

            const maxViewportDims = this.getParameter(gl, gl.MAX_VIEWPORT_DIMS);
            if (maxViewportDims && (maxViewportDims[0] < 2048 || maxViewportDims[1] < 2048)) {
                hints.push('Suspiciously low MAX_VIEWPORT_DIMS');
            }

        } catch (error) {
            hints.push(`Error detecting virtualization: ${error.message}`);
        }

        return hints;
    }

    /**
     * Calcula hash dos pixels
     */
    async hashPixels(pixels) {
        try {
            if (crypto && crypto.subtle) {
                const hashBuffer = await crypto.subtle.digest('SHA-256', pixels);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }
        } catch (error) {
            this.logger.debug('Web Crypto API não disponível para hash de pixels');
        }

        // Fallback para hash simples
        let hash = 0;
        for (let i = 0; i < pixels.length; i += 4) {
            hash = ((hash << 5) - hash) + pixels[i];
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16);
    }

    /**
     * Calcula checksum dos pixels
     */
    calculatePixelChecksum(pixels) {
        let checksum = 0;
        for (let i = 0; i < pixels.length; i += 16) {
            checksum += pixels[i];
        }
        return checksum;
    }

    /**
     * Calcula hash do fingerprint completo
     */
    async hashFingerprint(fingerprint) {
        const data = JSON.stringify({
            version: fingerprint.version,
            vendor: fingerprint.vendor,
            renderer: fingerprint.renderer,
            extensions: fingerprint.extensions.supported,
            parameters: fingerprint.parameters
        });

        try {
            if (crypto && crypto.subtle) {
                const encoder = new TextEncoder();
                const dataBuffer = encoder.encode(data);
                const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }
        } catch (error) {
            this.logger.debug('Web Crypto API não disponível para hash de fingerprint');
        }

        // Fallback para hash simples
        let hash = 0;
        for (let i = 0; i < data.length; i++) {
            const char = data.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16);
    }

    /**
     * Atualiza estatísticas de performance
     */
    updateStats(executionTime) {
        const totalTime = this.stats.avgExecutionTime * (this.stats.calls - 1) + executionTime;
        this.stats.avgExecutionTime = totalTime / this.stats.calls;
    }

    /**
     * Obtém estatísticas do serviço
     */
    getStats() {
        return {
            ...this.stats,
            cacheSize: this.cache.size,
            successRate: this.stats.calls > 0 ?
                ((this.stats.calls - this.stats.errors) / this.stats.calls * 100).toFixed(2) + '%' : '0%'
        };
    }

    /**
     * Limpa cache
     */
    clearCache() {
        this.cache.clear();
        this.logger.debug('Cache do WebGLService limpo');
    }

    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        this.config = newConfig;
        if (!newConfig.webglEnabled) {
            this.clearCache();
        }
        this.logger.debug('Configuração do WebGLService atualizada');
    }

    /**
     * Testa suporte ao WebGL
     */
    static isSupported() {
        try {
            const canvas = CanvasFactory.createCanvas();
            const gl = canvas.getContext('webgl') ||
                canvas.getContext('experimental-webgl') ||
                canvas.getContext('webgl2');
            return !!gl;
        } catch {
            return false;
        }
    }

    /**
     * Obtém capacidades do WebGL
     */
    static getCapabilities() {
        if (!WebGLService.isSupported()) {
            return { supported: false };
        }

        const canvas = CanvasFactory.createCanvas();
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

        return {
            supported: true,
            version: gl instanceof WebGL2RenderingContext ? 'WebGL 2.0' : 'WebGL 1.0',
            vendor: gl.getParameter(gl.VENDOR),
            renderer: gl.getParameter(gl.RENDERER),
            maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
            maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
            extensionCount: (gl.getSupportedExtensions() || []).length
        };
    }

    /**
     * Limpa recursos
     */
    destroy() {
        this.clearCache();

        if (this.glContext) {
            // Limpa contexto WebGL
            const gl = this.glContext;
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

            // Perde contexto se possível
            const loseContext = gl.getExtension('WEBGL_lose_context');
            if (loseContext) {
                loseContext.loseContext();
            }

            this.glContext = null;
        }

        if (this.canvas) {
            this.canvas.width = 1;
            this.canvas.height = 1;
            this.canvas = null;
        }

        this.logger.debug('WebGLService destruído');
    }
}